import { useCallback, useMemo } from 'react';
import type React from 'react';
import _ from 'lodash';

import type { FilterBlockKey, FilterSidePanelProps, PotencyRange } from '@dutchiesdk/ecommerce-extensions-sdk';
import useProductQueryParams from 'hooks/use-product-query-params';
import useSpecialMenuCards from 'src/dispensary/hooks/use-special-menu-cards';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';
import {
  mapToFilterSidePanelProps,
  type FilterSidePanelQueryParams,
} from 'src/theme/provider/commerce-components/bridge/bridge-map-filter-side-panel';
import { useCommerceComponents } from 'src/theme/provider/commerce-components/context';
import { toggleSpecialFilter } from 'utils/helpers/special-filter';
import type { FilterVisibility, MenuFilterOptions, SidebarCategory } from './types';

export const DefaultSidePanelSentinel: React.FC<FilterSidePanelProps> = () => null;
export const DefaultSidePanelPendingSentinel: React.FC<FilterSidePanelProps> = () => null;

const QUERY_PARAMS_BY_FILTER: Record<FilterBlockKey, string> = {
  brands: 'brands',
  effects: 'effects',
  specials: 'specialFilter',
  strainTypes: 'straintypes',
  subcategories: 'subcategories',
  terpenes: 'terpenes',
};

export type FilterSidePanelState = 'custom' | 'native' | 'pending';

type UseFilterSidePanelInput = {
  category?: SidebarCategory | null;
  context: FilterSidePanelProps['context'];
  fallback?: React.ComponentType<any>;
  globalCategoriesSubcategories?: MenuFilterOptions['categoriesSubcategories'];
  isLoading: boolean;
  menuFilterOptions: MenuFilterOptions;
  visibility: FilterVisibility;
};

type UseFilterSidePanelResult = {
  /** Remote SDK component when a theme registers FilterSidePanel, otherwise null. */
  CustomSidePanel: React.ComponentType<FilterSidePanelProps> | null;
  /** Mapped props for the remote panel; null when no custom panel is registered. */
  filterSidePanelProps: FilterSidePanelProps | null;
  /** Distinct resolution state: pending (theme loading), custom (registered in theme), or native. */
  sidePanelState: FilterSidePanelState;
};

/**
 * Resolves an SDK-provided FilterSidePanel override and, when present, builds
 * its props from host filter state. All URL query-param semantics stay in the
 * host: the remote component only receives data and host-bound callbacks.
 */
export function useFilterSidePanel({
  category,
  context,
  fallback,
  globalCategoriesSubcategories,
  isLoading,
  menuFilterOptions,
  visibility,
}: UseFilterSidePanelInput): UseFilterSidePanelResult {
  const { components } = useCommerceComponents();
  const { defaults, queryParams, setQueryParams } = useProductQueryParams();
  const hideSalesLanguage = useHideSalesLanguage();

  const fallbackComponent = useMemo(
    () => (fallback ?? DefaultSidePanelSentinel) as React.FC<FilterSidePanelProps>,
    [fallback]
  );

  const ResolvedSidePanel = useMemo(
    () =>
      components.FilterSidePanel({
        fallback: fallbackComponent,
        pending: DefaultSidePanelPendingSentinel,
      }),
    [components, fallbackComponent]
  );

  const sidePanelState: FilterSidePanelState = useMemo(() => {
    if (ResolvedSidePanel === DefaultSidePanelPendingSentinel) {
      return 'pending';
    }
    if (ResolvedSidePanel === fallbackComponent || ResolvedSidePanel === DefaultSidePanelSentinel) {
      return 'native';
    }
    return 'custom';
  }, [ResolvedSidePanel, fallbackComponent]);

  const hasCustomSidePanel = sidePanelState === 'custom';

  const { specials } = useSpecialMenuCards({ skip: !hasCustomSidePanel || !visibility.showSpecialsFilter });

  const { isMultiSelectSubcategoryFilterEnabled } = visibility;

  const handleToggleFilter = useCallback(
    (filter: FilterBlockKey, value: string) => {
      const param = QUERY_PARAMS_BY_FILTER[filter];
      if (!param) {
        return;
      }

      if (filter === 'subcategories' && !isMultiSelectSubcategoryFilterEnabled) {
        setQueryParams({ subcategories: [value], page: defaults.page });
        return;
      }

      const selected: string[] = queryParams[param] ?? [];
      const next = filter === 'specials' ? toggleSpecialFilter(selected, value) : _.xor(selected, [value]);
      setQueryParams({ [param]: filter === 'specials' && next.length === 0 ? undefined : next, page: defaults.page });
    },
    [defaults.page, isMultiSelectSubcategoryFilterEnabled, queryParams, setQueryParams]
  );

  const handleSetWeight = useCallback(
    (value: string | null) => setQueryParams({ weight: value ?? defaults.weight, page: defaults.page }),
    [defaults.page, defaults.weight, setQueryParams]
  );

  const handleSetPotencyRange = useCallback(
    (filter: 'potencyCbd' | 'potencyThc', range: PotencyRange) => {
      const param = filter === 'potencyThc' ? 'potencythc' : 'potencycbd';
      setQueryParams({ [param]: range, page: defaults.page });
    },
    [defaults.page, setQueryParams]
  );

  const handleSetCategory = useCallback(
    (value: string | null) =>
      setQueryParams({
        category: value ?? defaults.category,
        subcategories: defaults.subcategories,
        page: defaults.page,
      }),
    [defaults.category, defaults.page, defaults.subcategories, setQueryParams]
  );

  const handleSetSubcategories = useCallback(
    (values: string[]) => {
      const next = isMultiSelectSubcategoryFilterEnabled ? values : values.slice(-1);
      setQueryParams({ subcategories: next, page: defaults.page });
    },
    [defaults.page, isMultiSelectSubcategoryFilterEnabled, setQueryParams]
  );

  const handleClearAll = useCallback(
    () =>
      setQueryParams({
        brands: defaults.brands,
        category: defaults.category,
        effects: defaults.effects,
        potencycbd: defaults.potencycbd,
        potencythc: defaults.potencythc,
        specialFilter: defaults.specialFilter,
        straintypes: defaults.straintypes,
        subcategories: defaults.subcategories,
        terpenes: defaults.terpenes,
        weight: defaults.weight,
        page: defaults.page,
      }),
    [defaults, setQueryParams]
  );

  const filterSidePanelProps = useMemo(
    () =>
      !hasCustomSidePanel
        ? null
        : mapToFilterSidePanelProps({
            callbacks: {
              onClearAll: handleClearAll,
              onSetCategory: handleSetCategory,
              onSetPotencyRange: handleSetPotencyRange,
              onSetSubcategories: handleSetSubcategories,
              onSetWeight: handleSetWeight,
              onToggleFilter: handleToggleFilter,
            },
            categoryKey: category?.key,
            context,
            globalCategoriesSubcategories,
            hideSalesLanguage,
            isLoading,
            menuFilterOptions,
            queryParams: queryParams as FilterSidePanelQueryParams,
            specials,
            visibility,
          }),
    [
      category?.key,
      context,
      globalCategoriesSubcategories,
      handleClearAll,
      handleSetCategory,
      handleSetPotencyRange,
      handleSetSubcategories,
      handleSetWeight,
      handleToggleFilter,
      hasCustomSidePanel,
      hideSalesLanguage,
      isLoading,
      menuFilterOptions,
      queryParams,
      specials,
      visibility,
    ]
  );

  return {
    CustomSidePanel: hasCustomSidePanel ? ResolvedSidePanel : null,
    filterSidePanelProps,
    sidePanelState,
  };
}
